import { Box, Flex, ScrollArea, SimpleGrid, Text, Tooltip } from '@mantine/core';
import { BNNumberInput } from '../../components/NumberInput/NumberInput';
import { StaggerByValueInput } from '../../components/AdjustmentInput/StaggerByValueInput';
import { useNowPricing } from './NowPricing.hooks';
import { AdjustmentValueInputDiagonal } from '../../components/AdjustmentInput/AdjustmentValueInputDiagonal';
import { BNSwitch } from '../../components/Switch/Switch';
import { BNTextInput } from '../../components/TextInput/TextInput';
import { useFlag } from '@unleash/proxy-client-react';
import { BrokerNerdsBarkerCoreEnumsAdjustmentType } from '../../api';
import { useGlobalState } from '../../data/Global.state';
import { BNButton } from '../../components/Button/Button';
import { SoftFloorInput } from '../../components/SoftFloor/SoftFloor';
import WarningPauseIcon from '../../components/icons/WarningPause';
import { ErrorBoundary } from 'react-error-boundary';
import { useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { BNSelect } from '../../components/Select/Select';
import VerticalAlignBottomIcon from '../../components/icons/VerticalAlignBottom';
import TrendingUpIcon from '../../components/icons/TrendingUp';
import TrendingDownIcon from '../../components/icons/TrendingDown';
import BNInputGroup from '../../components/Group/InputGroup';
import TrackChangesIcon from '../../components/icons/TrackChanges';
import { formatCurrency } from '../../utils/formatters';
import { getTrailingFloorDisplayValue, getTrailingFloorRuleValue, isValidTrailingFloor, normalizeTrailingFloor } from '../../utils/trailing-floor';
import adjustmentInputClasses from '../../components/AdjustmentInput/AdjustmentValueInputDiagonal.module.css';

type FloorMode = 'standard' | 'trailing' | 'soft';

const getFloorModeIcon = (mode: FloorMode, color = 'currentColor') => {
  if (mode === 'trailing') {
    return <TrendingUpIcon size={16} color={color} />;
  }

  if (mode === 'soft') {
    return <TrendingDownIcon size={16} color={color} />;
  }

  return <VerticalAlignBottomIcon size={16} color={color} />;
};

const getFloorModeLabel = (mode: FloorMode) => {
  if (mode === 'trailing') {
    return 'Trailing';
  }

  if (mode === 'soft') {
    return 'Soft';
  }

  return 'Standard';
};

export const RuleForm = () => {
  const { form, isGroupedListing, isFormReady, setAutoUpdateRuleOnTargetComparableChanges, onSave, targetComparablePrice } = useNowPricing(
    'form',
    'isGroupedListing',
    'isFormReady',
    'setAutoUpdateRuleOnTargetComparableChanges',
    'onSave',
    'targetComparablePrice',
  );

  const hasSoftFloorFlag = useFlag('soft-floor-setting');
  const hasTrailingFloorFlag = useFlag('trailing-floor');

  const { principal } = useGlobalState('principal');
  const hasSoftFloorFeatureEnabled = hasSoftFloorFlag && (principal?.settings?.pricerSettings?.featureFlags?.softFloor ?? false);
  const hasTrailingFloorFeatureEnabled = hasTrailingFloorFlag && (principal?.settings?.pricerSettings?.featureFlags?.trailingFloor ?? false);
  const hasSoftFloorOnRule = !!form.values.softFloor;
  const hasTrailingFloorOnRule = !!form.values.trailingFloor;
  const softFloorEnabled = (form.values.softFloor?.value ?? 0) > 0;
  const canUseSoftFloor = hasSoftFloorFeatureEnabled || hasSoftFloorOnRule;
  const canUseTrailingFloor = hasTrailingFloorFeatureEnabled || hasTrailingFloorOnRule;
  const showAdvancedFloorInput = canUseSoftFloor || canUseTrailingFloor;
  const floorMode: FloorMode = hasTrailingFloorOnRule ? 'trailing' : hasSoftFloorOnRule ? 'soft' : 'standard';

  const defaultSoftFloor =
    principal?.settings?.pricerSettings?.softFloor?.value && principal.settings.pricerSettings.softFloor.value > 0
      ? principal.settings.pricerSettings.softFloor
      : { period: '1.00:00:00', typeId: BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage, value: 1.01 };

  const principalTrailingFloor = normalizeTrailingFloor(principal?.settings?.pricerSettings?.trailingFloor);
  const defaultTrailingFloor = isValidTrailingFloor(principalTrailingFloor) ? principalTrailingFloor : { typeId: BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount, value: -1 };

  const trailingFloorDisplayValue = getTrailingFloorDisplayValue(form.values.trailingFloor);
  const pauseAutoPricingFlag = useFlag('pause-autopricing');
  const [error, setError] = useState(0);
  const floorModeOptions = [
    { value: 'standard', label: '' },
    ...(canUseTrailingFloor ? [{ value: 'trailing', label: '' }] : []),
    ...(canUseSoftFloor ? [{ value: 'soft', label: '' }] : []),
  ];

  const setFloorMode = (mode: FloorMode) => {
    if (mode === 'standard') {
      form.setValues({
        ...form.values,
        softFloor: undefined,
        trailingFloor: undefined,
      });
      return;
    }

    if (mode === 'soft') {
      form.setValues({
        ...form.values,
        trailingFloor: undefined,
        softFloor: softFloorEnabled ? form.values.softFloor : defaultSoftFloor,
      });
      return;
    }

    setAutoUpdateRuleOnTargetComparableChanges(false);
    form.setValues({
      ...form.values,
      softFloor: undefined,
      trailingFloor: hasTrailingFloorOnRule ? form.values.trailingFloor : defaultTrailingFloor,
    });
  };

  return (
    <ErrorBoundary
      FallbackComponent={() => <div>Error</div>}
      resetKeys={[error]}
      onError={(err) => {
        datadogRum.addError(err);
        if (!import.meta.env.DEV) {
          setError(error + 1);
        }
      }}
    >
      <Box pos={pauseAutoPricingFlag || !!form.values.warningNotes ? 'relative' : 'static'}>
        <SimpleGrid cols={2} spacing="sm" my={8}>
          <AdjustmentValueInputDiagonal
            label="Adjustment"
            numInputWidth={65}
            disabled={!isFormReady}
            onChange={(value) => {
              form.getInputProps('adjustmentValue').onChange(value.value);
              form.getInputProps('adjustmentTypeId').onChange(value.type);
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onSave();
              }
            }}
            value={form.values.adjustmentValue || 0}
            type={form.values.adjustmentTypeId || 'Amount'}
            error={form.errors.adjustmentValue}
          />

          <BNNumberInput
            selectOnFocus
            disabled={!isFormReady}
            {...form.getInputProps('numComparables')}
            label="# Comparables"
            decimalScale={0}
            min={1}
            size="xs"
            defaultValue={1}
          />
        </SimpleGrid>
        <SimpleGrid mb={8} cols={2} spacing="sm">
          {!showAdvancedFloorInput && (
            <BNNumberInput
              selectOnFocus
              disabled={!isFormReady}
              leftSection={<Text size="xs">$</Text>}
              {...form.getInputProps('floorPrice')}
              onChange={(value) => {
                setAutoUpdateRuleOnTargetComparableChanges(false);
                form.getInputProps('floorPrice').onChange(typeof value === 'string' ? undefined : value);
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  onSave();
                }
              }}
              decimalScale={2}
              fixedDecimalScale
              step={1}
              stepHoldDelay={500}
              stepHoldInterval={100}
              min={0}
              size="xs"
              label="Floor"
            />
          )}
          {showAdvancedFloorInput && (
            // Does not hover correctly
            <BNInputGroup label="Floor" error={form.errors.floorPrice}>
              <BNInputGroup.Item w={36}>
                <BNSelect
                  className={`${adjustmentInputClasses.middleInput} ${adjustmentInputClasses.shortSelectRoot}`}
                  size="xs"
                  disabled={!isFormReady}
                  value={floorMode}
                  dropdownWidth={160}
                  rightSectionWidth={20}
                  comboboxProps={{ styles: { option: { paddingLeft: 4, paddingRight: 4, display: 'flex', justifyContent: 'center' } } }}
                  onChange={(value) => {
                    if (value) {
                      setFloorMode(value as FloorMode);
                    }
                  }}
                  data={floorModeOptions}
                  withCheckIcon // what does this do?
                  leftSection={
                    <Flex pl={2} align="center" h="100%">
                      {getFloorModeIcon(floorMode, 'var(--colors-paperReverse)')}
                    </Flex>
                  }
                  leftSectionPointerEvents="none"
                  leftSectionWidth={18}
                  styles={{
                    input: {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                  }}
                  renderOption={({ option }) => (
                    <Flex align="center" gap={6}>
                      {getFloorModeIcon(option.value as FloorMode)}
                      <Text size="xs" style={{ whiteSpace: 'nowrap' }}>
                        {getFloorModeLabel(option.value as FloorMode)}
                      </Text>
                    </Flex>
                  )}
                />
              </BNInputGroup.Item>
              <BNInputGroup.Item w={93}>
                <BNNumberInput
                  selectOnFocus
                  disabled={!isFormReady || floorMode === 'trailing'}
                  leftSection={floorMode === 'trailing' ? undefined : <Text size="xs">$</Text>}
                  prefix={floorMode === 'trailing' ? '$' : undefined}
                  {...form.getInputProps('floorPrice')}
                  onChange={(value) => {
                    setAutoUpdateRuleOnTargetComparableChanges(false);
                    form.getInputProps('floorPrice').onChange(typeof value === 'string' ? undefined : value);
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      onSave();
                    }
                  }}
                  decimalScale={2}
                  fixedDecimalScale
                  step={1}
                  stepHoldDelay={500}
                  stepHoldInterval={100}
                  min={0}
                  size="xs"
                  styles={{
                    input: {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    },
                  }}
                />
              </BNInputGroup.Item>
            </BNInputGroup>
          )}
          <BNNumberInput
            selectOnFocus
            disabled={!isFormReady}
            {...form.getInputProps('ceilingPrice')}
            onChange={(value) => {
              setAutoUpdateRuleOnTargetComparableChanges(false);
              form.getInputProps('ceilingPrice').onChange(typeof value === 'string' ? undefined : value);
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onSave();
              }
            }}
            leftSection={<Text size="xs">$</Text>}
            label="Ceiling"
            decimalScale={2}
            fixedDecimalScale
            step={1}
            stepHoldDelay={500}
            stepHoldInterval={100}
            min={0}
            size="xs"
          />
        </SimpleGrid>
        {floorMode === 'soft' && form.values.softFloor && (
          <Box pb={isGroupedListing ? 8 : 0}>
            <SoftFloorInput
              min={0.01}
              disabled={!isFormReady}
              floorError={form.errors['softFloor.value']}
              softFloor={form.values.softFloor}
              onFloorChange={(value) => {
                form.setValues({
                  ...form.values,
                  softFloor: {
                    ...form.values.softFloor,
                    value: value.value ?? 0,
                    typeId: value.type,
                    period: form.values.softFloor?.period ?? '1.00:00:00',
                  },
                });
              }}
              periodError={form.errors['softFloor.period']}
              onPeriodChange={(value) => {
                form.setFieldValue('softFloor.period', value);
              }}
              onSave={onSave}
            />
          </Box>
        )}
        {floorMode === 'trailing' && form.values.trailingFloor && (
          <SimpleGrid mb={8} cols={2} spacing="sm">
            {/* Hovers correctly */}
            <BNInputGroup label="Trailing Floor" error={form.errors['trailingFloor.value']}>
              <BNInputGroup.Item w={36}>
                <BNSelect
                  className={`${adjustmentInputClasses.middleInput} ${adjustmentInputClasses.shortSelectRoot}`}
                  size="xs"
                  disabled={!isFormReady}
                  value={form.values.trailingFloor.typeId}
                  dropdownWidth={40}
                  rightSectionWidth={20}
                  comboboxProps={{ styles: { option: { paddingLeft: 4, paddingRight: 4, display: 'flex', justifyContent: 'center' } } }}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }
                    const nextType = value as BrokerNerdsBarkerCoreEnumsAdjustmentType;
                    form.setFieldValue('trailingFloor', {
                      typeId: nextType,
                      value: getTrailingFloorRuleValue(trailingFloorDisplayValue, nextType),
                    });
                  }}
                  data={[
                    { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount, label: '$' },
                    { value: BrokerNerdsBarkerCoreEnumsAdjustmentType.Percentage, label: '%' },
                  ]}
                  styles={{
                    input: {
                      paddingLeft: 6,
                      paddingRight: 0,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  }}
                />
              </BNInputGroup.Item>
              <BNInputGroup.Item w={159}>
                <BNNumberInput
                  selectOnFocus
                  disabled={!isFormReady}
                  value={trailingFloorDisplayValue}
                  onChange={(value) => {
                    const displayValue = typeof value === 'string' || value === undefined ? 0 : value;
                    const typeId = form.values.trailingFloor?.typeId ?? BrokerNerdsBarkerCoreEnumsAdjustmentType.Amount;

                    form.setFieldValue('trailingFloor.value', getTrailingFloorRuleValue(displayValue, typeId));
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      onSave();
                    }
                  }}
                  allowNegative
                  decimalScale={2}
                  fixedDecimalScale
                  step={1}
                  stepHoldDelay={500}
                  stepHoldInterval={100}
                  max={0}
                  size="xs"
                  styles={{
                    input: {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    },
                  }}
                />
              </BNInputGroup.Item>
            </BNInputGroup>
            <Box ta="right">
              <Text size="xs" component="label" c="var(--colors-gray-5)" lh="1.25rem">
                Target Comparable
              </Text>
              <Box pos="relative" h={30}>
                <Box pos="absolute" left={0} top="50%" style={{ transform: 'translateY(-50%)' }}>
                  <TrackChangesIcon size={16} color="var(--colors-gray-5)" />
                </Box>
                <Text size="xs" c="var(--colors-gray-7)" lh="30px" pl={22}>
                  {targetComparablePrice == null ? '--' : formatCurrency(targetComparablePrice)}
                </Text>
              </Box>
            </Box>
          </SimpleGrid>
        )}
        {isGroupedListing && (
          <SimpleGrid mb={8} cols={2} spacing="sm">
            <Box pos="relative">
              <Text size="xs" c="var(--colors-gray-5)" component="label" lh="1.25rem">
                # Active
              </Text>
              <Tooltip withArrow label="Broadcast all listings in the group">
                <Box pos="absolute" top={6} right={0}>
                  <BNSwitch
                    checked={form.values.numActive === 2147483647}
                    onChange={() => form.setFieldValue('numActive', form.values.numActive === 2147483647 ? 1 : 2147483647)}
                    size="xxs"
                    label="ALL"
                  />
                </Box>
              </Tooltip>
              {!!form.values.numActive && form.values.numActive >= 2147483647 && <BNTextInput disabled size="xs" value="All Listings" />}
              {(form.values.numActive === undefined || form.values.numActive === null || form.values.numActive < 2147483647) && (
                <BNNumberInput disabled={!isFormReady} {...form.getInputProps('numActive')} decimalScale={0} fixedDecimalScale allowDecimal={false} min={0} step={1} size="xs" />
              )}
            </Box>
            <StaggerByValueInput
              numInputWidth={78}
              disabled={!isFormReady}
              label="Stagger By"
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  onSave();
                }
              }}
              onChange={(value) => {
                form.getInputProps('staggerByValue').onChange(value.value);
                form.getInputProps('staggerByTypeId').onChange(value.type);
              }}
              value={form.values.staggerByValue || 0}
              type={form.values.staggerByTypeId || 'Amount'}
            />
          </SimpleGrid>
        )}
        {!!form.values.warningNotes && (
          <Flex
            pos="absolute"
            direction="column"
            top={4}
            bottom={-4}
            left={0}
            right={0}
            bg="var(--colors-paper)"
            style={{ border: '1px solid var(--colors-yellow-5)', boxShadow: 'var(--mantine-shadow-md)', borderRadius: 3, zIndex: 1, overflow: 'hidden' }}
          >
            <Flex align="center" h={30} px={6} bg="var(--colors-yellow-0)" c="var(--colors-yellow-warning)" fz={12} style={{ borderRadius: '3px 3px 0 0' }}>
              <WarningPauseIcon /> &nbsp;Auto-Price Warning Note
            </Flex>
            <ScrollArea type="always" scrollbars="y" flex={1}>
              <Box px="xs" py={4} pr="md">
                <Text>{form.values.warningNotes}</Text>
              </Box>
            </ScrollArea>
            <Flex py={8} px="xs" style={{ borderTop: '1px solid var(--colors-gray-2)' }}>
              <BNButton
                variant="filled"
                color="green"
                size="compact-xs"
                flex={1}
                onClick={() => {
                  form.setFieldValue('warningNotes', null);
                }}
              >
                I Understand, Dismiss This Warning
              </BNButton>
            </Flex>
          </Flex>
        )}
      </Box>
    </ErrorBoundary>
  );
};
